import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, Tag as TagIcon } from "lucide-react";
import { api } from "../lib/api";
import PageHero from "../components/PageHero";
import { useReveal } from "../lib/motion";

export default function Blog() {
    const { t } = useTranslation();
    const [posts, setPosts] = useState([]);
    const [tags, setTags] = useState([]);
    const [category, setCategory] = useState("all");
    const [tag, setTag] = useState(null);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    useEffect(() => {
        api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();
        if (category !== "all") params.set("category", category);
        if (tag) params.set("tag", tag);
        if (search) params.set("search", search);
        params.set("limit", "50");
        api.get(`/posts?${params.toString()}`).then((r) => setPosts(r.data)).catch(console.error);
    }, [category, tag, search]);

    const submitSearch = (e) => { e.preventDefault(); setSearch(searchInput.trim()); };
    const clearSearch = () => { setSearch(""); setSearchInput(""); };
    const categories = ["all", "Cycle Health", "Fertility", "Pregnancy", "Wellness", "Technology", "Community"];

    useReveal([posts.length]);

    return (
        <div data-testid="blog-page">
            <PageHero
                testId="blog-hero"
                overline={t("blog.section")}
                title={
                    <>
                        {t("blog.title1")}<br />
                        <span className="italic font-light">{t("blog.title2")}</span>
                    </>
                }
                subtitle={t("blog.sub")}
                size="lg"
            >
                <form onSubmit={submitSearch} className="max-w-xl relative" data-testid="blog-search-form">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-femcare-accent" />
                    <input
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t("blog.searchPh")}
                        className="w-full glass rounded-full pl-12 pr-12 py-3 focus:outline-none focus:border-femcare-pink text-femcare-purple placeholder-femcare-purple/40"
                        data-testid="blog-search-input"
                    />
                    {(searchInput || search) && (
                        <button type="button" onClick={clearSearch} className="absolute right-4 top-1/2 -translate-y-1/2 text-femcare-purple/40 hover:text-femcare-pink" data-testid="blog-search-clear">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </form>
            </PageHero>

            <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-8 border-y border-femcare-border py-6">
                <div className="flex flex-wrap gap-3" data-testid="blog-category-filters">
                    {categories.map((c) => (
                        <button
                            key={c} onClick={() => setCategory(c)}
                            className={`text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition ${
                                category === c
                                    ? 'bg-femcare-pink text-white border-femcare-pink'
                                    : 'bg-white text-femcare-purple/70 border-femcare-border hover:border-femcare-pink hover:text-femcare-pink'
                            }`}
                            data-testid={`category-${c.replace(/ /g, '-')}`}
                        >
                            {c === "all" ? t("common.all") : c}
                        </button>
                    ))}
                </div>
            </section>

            {tags.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
                    <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-3 flex items-center gap-2">
                        <TagIcon className="w-3 h-3" /> {t("blog.filterByTag")}
                    </p>
                    <div className="flex flex-wrap gap-2" data-testid="blog-tag-filters">
                        {tag && (
                            <button onClick={() => setTag(null)}
                                className="text-xs px-3 py-1 rounded-full bg-femcare-purple text-white inline-flex items-center gap-1"
                                data-testid="blog-tag-clear">
                                {tag} <X className="w-3 h-3" />
                            </button>
                        )}
                        {!tag && tags.slice(0, 18).map((tg) => (
                            <button key={tg.tag} onClick={() => setTag(tg.tag)}
                                className="text-xs px-3 py-1 rounded-full border border-femcare-border text-femcare-purple/70 hover:border-femcare-pink hover:text-femcare-pink transition"
                                data-testid={`tag-${tg.tag.replace(/ /g, '-')}`}>
                                #{tg.tag} <span className="text-femcare-purple/40">({tg.count})</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {posts.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16 pt-6">
                    <Link to={`/blog/${posts[0].slug}`} className="grid grid-cols-1 md:grid-cols-12 gap-8 group" data-testid="featured-post">
                        <div className="md:col-span-7">
                            <div className="aspect-[16/10] rounded-3xl overflow-hidden bg-femcare-muted">
                                <img src={posts[0].cover_image} alt={posts[0].title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            </div>
                        </div>
                        <div className="md:col-span-5 flex flex-col justify-center">
                            <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-4">
                                {t("blog.featured")} · {posts[0].category} · {posts[0].read_time} {t("common.min")}
                            </p>
                            <h2 className="editorial-headline text-3xl md:text-4xl lg:text-5xl text-femcare-purple group-hover:text-femcare-pink transition-colors mb-5">
                                {posts[0].title}
                            </h2>
                            <p className="text-femcare-purple/70 leading-relaxed mb-6">{posts[0].excerpt}</p>
                            <p className="text-sm text-femcare-purple/60">{t("common.by")} {posts[0].author}</p>
                        </div>
                    </Link>
                </section>
            )}

            <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                    {posts.slice(1).map((p) => (
                        <Link key={p.id} to={`/blog/${p.slug}`} className="group" data-testid={`blog-card-${p.slug}`}>
                            <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-5 bg-femcare-muted">
                                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            </div>
                            <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-3">
                                {p.category} · {p.read_time} {t("common.min")}
                            </p>
                            <h3 className="font-serif text-2xl text-femcare-purple group-hover:text-femcare-pink transition-colors leading-tight mb-3">
                                {p.title}
                            </h3>
                            <p className="text-sm text-femcare-purple/60 line-clamp-2 mb-3">{p.excerpt}</p>
                            <div className="flex items-center justify-between">
                                <p className="text-xs text-femcare-purple/50">{t("common.by")} {p.author}</p>
                                {p.tags && p.tags.length > 0 && (
                                    <p className="text-xs text-femcare-accent">#{p.tags[0]}</p>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>

                {posts.length === 0 && (
                    <p className="text-center text-femcare-purple/60 py-16" data-testid="blog-no-results">{t("blog.noResults")}</p>
                )}
            </section>
        </div>
    );
}
